import type { PeerConnectionEvent } from 'jssip/lib/RTCSession';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { closeAndCleanupChannel } from './dataChannelHelpers';

import { consoleLogDev } from 'common/helpers/consoleLogDev';
import { useAppDispatch } from 'features/app/hooks';
import { dataChannelLabel } from 'features/sip/constants';
import {
  createDataChannelAnalyticsTracker,
  handleDataChannelClosed,
  logDataChannelCreationException,
  logDataChannelError,
  logDataChannelSendFailed,
  sendCreateDataChannelSkippedAnalytics,
  sendDataChannelClosingAnalytics,
  sendDataChannelOpenedAnalytics,
  toArrayBuffer,
  validateDataChannelMessage,
  type DataChannelMetrics,
} from 'features/sip/helpers';
import { VrsChatStatus } from 'features/vrs-chat/enums';
import { VrsChatEventBus } from 'features/vrs-chat/services';
import { setVrsChatStatus } from 'features/vrs-chat/store';

// U+FEFF (ZERO WIDTH NO-BREAK SPACE): T.140 keep-alive marker per RFC 4103 §4.6.
// Receivers are required to not present it, so it's safe to send periodically
// to keep the gateway's data channel path warm without producing visible text.
const T140_KEEPALIVE_CHAR = '\ufeff';
const T140_KEEPALIVE_BYTES = new TextEncoder().encode(T140_KEEPALIVE_CHAR);
const KEEPALIVE_INTERVAL_MS = 30_000;

interface UseDataChannelReturn {
  createDataChannel: (peerConnectionEvent: PeerConnectionEvent) => void;
  closeDataChannel: () => void;
  sendMessage: (message: Uint8Array) => boolean;
}

const ANALYTICS_DEBOUNCE_MS = 5000;

// ============================================================================
// HOOK
// ============================================================================

/**
 * Manages WebRTC DataChannel lifecycle for real-time bidirectional messaging in VRS calls.
 *
 * **Primary Purpose**: Establishes and maintains a reliable data channel for VRS chat messages
 * between peers during active SIP sessions, handling connection states, message routing, and
 * analytics tracking.
 *
 * **Inputs**:
 * - `peerConnectionEvent` (via `createDataChannel`): JsSIP RTCSession peer connection event
 *   containing the WebRTC peer connection to attach the data channel to
 *
 * **Outputs** (via `UseDataChannelReturn`):
 * - `createDataChannel`: Function to establish a new data channel on a peer connection
 * - `closeDataChannel`: Function to cleanly tear down the data channel and reset state
 * - `sendMessage`: Function to send binary messages through the channel (returns success boolean)
 *
 * **Reusability**: Single-use per call session. Designed to used within useSipSessionHandler hook.
 * Not intended for multiple concurrent instances.
 *
 * **Key Behaviors**:
 * - Prevents concurrent channel creation attempts
 * - Tracks send/receive metrics with debounced analytics
 * - Routes received messages to VrsChatEventBus
 * - Manages VRS chat Redux state (connected/disconnected status)
 * - Handles graceful cleanup with metrics logging
 */
export const useDataChannel = (): UseDataChannelReturn => {
  const dispatch = useAppDispatch();

  // Refs
  const channelRef = useRef<RTCDataChannel | null>(null);
  const cleanupInProgressRef = useRef<boolean>(false);
  const keepaliveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const metricsRef = useRef<DataChannelMetrics>({
    messagesReceived: 0,
    messagesSent: 0,
  });

  // Analytics Trackers - initialize once using useMemo for clarity and React convention
  const trackers = useMemo(
    () => ({
      received: createDataChannelAnalyticsTracker(
        dispatch,
        'dataChannelMessagesReceived',
        (c) => `received ${c} messages`,
        ANALYTICS_DEBOUNCE_MS
      ),
      sent: createDataChannelAnalyticsTracker(
        dispatch,
        'dataChannelMessagesSent',
        (c) => `sent ${c} messages`,
        ANALYTICS_DEBOUNCE_MS
      ),
    }),
    [dispatch]
  );

  // ============================================================================
  // INTERNAL UTILS
  // ============================================================================

  const clearKeepaliveTimer = useCallback(() => {
    if (keepaliveTimerRef.current !== null) {
      clearInterval(keepaliveTimerRef.current);
      keepaliveTimerRef.current = null;
    }
  }, []);

  const startKeepaliveTimer = useCallback(
    (dataChannel: RTCDataChannel) => {
      clearKeepaliveTimer();
      keepaliveTimerRef.current = setInterval(() => {
        if (dataChannel.readyState !== 'open') {
          return;
        }
        try {
          dataChannel.send(toArrayBuffer(T140_KEEPALIVE_BYTES));
        } catch (error) {
          consoleLogDev('DEBUG: DataChannel keepalive send failed -', error);
        }
      }, KEEPALIVE_INTERVAL_MS);
    },
    [clearKeepaliveTimer]
  );

  const doCloseAndCleanupChannel = useCallback(
    (reason: string) => {
      clearKeepaliveTimer();
      closeAndCleanupChannel({
        dispatch,
        channelRef,
        cleanupInProgressRef,
        trackers,
        metricsRef,
        reason,
      });
    },
    [dispatch, trackers, clearKeepaliveTimer]
  );

  // ============================================================================
  // DATACHANNEL HANDLERS
  // ============================================================================

  const setupDataChannelHandlers = useCallback(
    (dataChannel: RTCDataChannel) => {
      // onOpen
      dataChannel.onopen = (event) => {
        dispatch(setVrsChatStatus(VrsChatStatus.Connected));
        consoleLogDev('DEBUG: DataChannel Open -', event);

        sendDataChannelOpenedAnalytics(dispatch, dataChannel);
        startKeepaliveTimer(dataChannel);
      };

      // onMessage
      dataChannel.onmessage = (event) => {
        const { data } = event;
        const isString = typeof data === 'string';

        if (isString) {
          if (!validateDataChannelMessage(data)) {
            return;
          }

          // Strip T.140 keep-alive markers (U+FEFF, RFC 4103 §4.6) so they
          // aren't rendered in chat. If the entire payload is keep-alive,
          // don't emit or count it as a chat message.
          const presentable = data.replace(/\ufeff/g, '');
          if (presentable.length === 0) {
            return;
          }

          consoleLogDev('DEBUG: DataChannel Message (string) -', event);
          VrsChatEventBus.$receiveMessage.next(presentable);
          trackers.received();
          metricsRef.current.messagesReceived++;
        } else {
          // Binary data
          if (!validateDataChannelMessage(data as ArrayBuffer)) {
            return;
          }

          consoleLogDev('DEBUG: DataChannel Message (binary) -', event);
          trackers.received();
          metricsRef.current.messagesReceived++;
        }
      };

      // 4. onClosing / onClose / onError
      dataChannel.onclosing = (event) => {
        sendDataChannelClosingAnalytics(dispatch, event, dataChannel);
      };

      dataChannel.onclose = (event) => {
        consoleLogDev('DEBUG: DataChannel Close -', event);
        clearKeepaliveTimer();
        handleDataChannelClosed(dispatch, dataChannel, metricsRef.current);

        dispatch(setVrsChatStatus(VrsChatStatus.Disconnected));
        doCloseAndCleanupChannel('onclose');
      };

      dataChannel.onerror = (event) => {
        consoleLogDev('DEBUG: DataChannel Error -', event);
        logDataChannelError(dispatch, event, dataChannel);
        // Note: do not close channel on error; let onclose handle it
        // some errors are non fatal and channel may recover.
      };
    },
    [
      clearKeepaliveTimer,
      dispatch,
      doCloseAndCleanupChannel,
      startKeepaliveTimer,
      trackers,
    ]
  );

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  const createDataChannel = useCallback(
    (peerConnectionEvent: PeerConnectionEvent) => {
      // Prevent creation during cleanup
      if (cleanupInProgressRef.current) {
        consoleLogDev('DEBUG: Cleanup in progress, ignoring createDataChannel');
        return;
      }

      // Guard: skip creation if we already have a healthy channel.
      const existingChannel = channelRef.current;
      if (
        existingChannel &&
        (existingChannel.readyState === 'open' ||
          existingChannel.readyState === 'connecting')
      ) {
        sendCreateDataChannelSkippedAnalytics(
          dispatch,
          existingChannel.readyState
        );
        return;
      }

      try {
        dispatch(setVrsChatStatus(VrsChatStatus.Connecting));
        consoleLogDev(
          'peerConnectionEvent, creating dataChannel',
          peerConnectionEvent
        );

        // Cleanup existing channel if present before creating new one
        if (channelRef.current) {
          doCloseAndCleanupChannel('replaced');
        }

        const dataChannel =
          peerConnectionEvent.peerconnection.createDataChannel(
            dataChannelLabel,
            {
              protocol: 't140',
              negotiated: true,
              id: 1,
            }
          );

        channelRef.current = dataChannel;
        setupDataChannelHandlers(dataChannel);
      } catch (error) {
        consoleLogDev('DEBUG: Create DataChannel Error -', error);
        const wrapped =
          error instanceof Error ? error : new Error(String(error));

        logDataChannelCreationException(dispatch, wrapped);

        dispatch(setVrsChatStatus(VrsChatStatus.FailedToConnect));
        doCloseAndCleanupChannel('create_exception');
      }
    },
    [dispatch, doCloseAndCleanupChannel, setupDataChannelHandlers]
  );

  const sendMessage = useCallback(
    (message: Uint8Array): boolean => {
      // Validate outgoing message
      if (!validateDataChannelMessage(message)) {
        return false;
      }

      const channel = channelRef.current;

      if (channel?.readyState === 'open') {
        try {
          channel.send(toArrayBuffer(message));
          trackers.sent();
          metricsRef.current.messagesSent++;
          return true;
        } catch (error) {
          consoleLogDev('DEBUG: Send failed -', error);
          logDataChannelSendFailed(dispatch, channel, message.byteLength);
          return false;
        }
      } else {
        consoleLogDev("DEBUG: couldn't send; channel not open", {
          channel,
          readyState: channel?.readyState,
        });
        return false;
      }
    },
    [dispatch, trackers]
  );

  const closeDataChannel = useCallback(() => {
    consoleLogDev('DEBUG: DataChannel Close (manual)');
    doCloseAndCleanupChannel('manual_close');
  }, [doCloseAndCleanupChannel]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Kill timers immediately before cleanup
      trackers.received.cancel();
      trackers.sent.cancel();
      clearKeepaliveTimer();

      // Use centralized cleanup for consistency
      if (channelRef.current) {
        doCloseAndCleanupChannel('unmount');
      }
      channelRef.current = null;
    };
  }, [trackers, doCloseAndCleanupChannel, clearKeepaliveTimer]);

  return {
    createDataChannel,
    closeDataChannel,
    sendMessage,
  };
};
